import { useState, useRef, useMemo } from "react";
import type { SectionList } from "react-native";
import { TConsultView } from "../../../../../utils/types";
import type { ConsultPatient, ConsultSection } from "./useConsultSearch";
import type { GalleryImageMetadata } from "../../../../common/native/GalleryModal";

/**
 * Centralized state management for the Consults feature
 * Extracts all useState declarations from HomeConsults
 */
export const useConsultState = () => {
  // View states
  const [currentView, setCurrentView] = useState<TConsultView>("baseView");
  const [selectedPatient, setSelectedPatient] = useState<ConsultPatient | null>(
    null,
  );
  const memoizedPatient = useMemo(() => selectedPatient, [selectedPatient?.id]);

  // Search states
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<ConsultSection[]>([]);
  const [searchMode, setSearchMode] = useState<boolean>(false);

  // Modal states
  const [galleryModalVisible, setGalleryModalVisible] =
    useState<boolean>(false);
  const [showQuickRecordModal, setShowQuickRecordModal] =
    useState<boolean>(false);
  const [rangePickerVisible, setRangePickerVisible] = useState<boolean>(false);
  const [showEhrSelectionModal, setShowEhrSelectionModal] =
    useState<boolean>(false);

  // Photo/Gallery states
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [selectedPhotoThumbnails, setSelectedPhotoThumbnails] = useState<
    string[]
  >([]);
  const [selectedPhotoEditTargets, setSelectedPhotoEditTargets] = useState<
    Array<{ targetType: "clinic_patient_image"; targetId: number } | null>
  >([]);
  const [selectedPhotoMetadata, setSelectedPhotoMetadata] = useState<
    Array<GalleryImageMetadata | null>
  >([]);
  const [selectedPatientName, setSelectedPatientName] = useState<string>("");
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);
  const [photoEditRefreshKey, setPhotoEditRefreshKey] = useState<number>(0);

  // Simulation states
  const [simulations, setSimulations] = useState<any[]>([]);
  const [voiceNoteRefreshKey, setVoiceNoteRefreshKey] = useState<number>(0);

  // UI states
  const [loading, setLoading] = useState<boolean>(false);
  const [visibleSectionIndex, setVisibleSectionIndex] = useState<number>(0);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [scrollY, setScrollY] = useState<number>(0);
  const [ehrConnected, setEhrConnected] = useState<boolean>(true);

  // Refs
  const scrollRef = useRef<SectionList<any> | null>(null);

  return {
    // View states
    currentView,
    setCurrentView,
    selectedPatient,
    setSelectedPatient,
    memoizedPatient,

    // Search states
    searchTerm,
    setSearchTerm,
    isSearching,
    setIsSearching,
    searchResults,
    setSearchResults,
    searchMode,
    setSearchMode,

    // Modal states
    galleryModalVisible,
    setGalleryModalVisible,
    showQuickRecordModal,
    setShowQuickRecordModal,
    rangePickerVisible,
    setRangePickerVisible,
    showEhrSelectionModal,
    setShowEhrSelectionModal,

    // Photo/Gallery states
    selectedPhotos,
    setSelectedPhotos,
    selectedPhotoThumbnails,
    setSelectedPhotoThumbnails,
    selectedPhotoEditTargets,
    setSelectedPhotoEditTargets,
    selectedPhotoMetadata,
    setSelectedPhotoMetadata,
    selectedPatientName,
    setSelectedPatientName,
    selectedPhotoIndex,
    setSelectedPhotoIndex,
    photoEditRefreshKey,
    setPhotoEditRefreshKey,

    // Simulation states
    simulations,
    setSimulations,
    voiceNoteRefreshKey,
    setVoiceNoteRefreshKey,

    // UI states
    loading,
    setLoading,
    visibleSectionIndex,
    setVisibleSectionIndex,
    headerHeight,
    setHeaderHeight,
    scrollY,
    setScrollY,
    ehrConnected,
    setEhrConnected,

    // Refs
    scrollRef,
  };
};
